import React, { useState, useRef, useEffect } from 'react';
import { ProgressBar } from '../ui/ProgressBar';
import { Modal } from '../ui/Modal';
import { calculateVPD } from '../../game/systems/environmental';
import { STRAINS_LIST } from '../../game/data/strains';
import { EQUIPMENT_CATALOG, RECIPE_PACKAGES } from '../../game/data/recipes';
import { getRecipesForStrain } from '../../game/data/strainRecipes';
import { motion } from 'framer-motion';
import { LabViewport } from '../ui/LabViewport';

const BASE = process.env.PUBLIC_URL + '/assets/';
const CACHE_V = '?v=2';

// ── Plant stage sprite selection ───────────────────────────────────────────────
function getPlantSprite(batch) {
  if (!batch) return null;
  const { stage, dayInStage } = batch;
  if (stage === 'seedling') {
    return dayInStage < 5 ? 'plants/stage-germination.png' : 'plants/stage-seedling.png';
  }
  if (stage === 'vegetative') {
    return dayInStage < 14 ? 'plants/stage-veg-early.png' : 'plants/stage-veg-mid.png';
  }
  if (stage === 'flowering') {
    return 'plants/stage-flower.png';
  }
  if (stage === 'harvest') {
    return 'plants/stage-harvest.png';
  }
  return 'plants/stage-seedling.png';
}

// ── Ambient tint from live environment ────────────────────────────────────────
function pctDevClass(actual, target) {
  const p = Math.abs((actual - target) / target) * 100;
  return p < 5 ? 'ok' : p < 15 ? 'warning' : 'danger';
}

function getAmbientTint(env, batch) {
  if (!env) return null;
  const lightOn = env.lightOn;
  const stage = batch?.stage;
  // Base tint from temperature deviation
  const temp = env.temperature || 24;
  if (lightOn && stage === 'flowering') return 'rgba(140, 60, 255, 0.07)';
  if (lightOn && stage === 'vegetative') return 'rgba(60, 180, 100, 0.07)';
  if (temp > 30) return 'rgba(255, 80, 40, 0.06)';
  if (temp < 18) return 'rgba(60, 120, 255, 0.06)';
  return 'rgba(60, 200, 120, 0.04)';
}

// ── Arc gauge for equipment health ────────────────────────────────────────────
function ArcGauge({ value, size = 72 }) {
  const pct  = Math.max(0, Math.min(100, value)) / 100;
  const color = value > 70 ? '#3fb950' : value > 40 ? '#d29922' : '#f85149';
  const r  = size / 2 - 7;
  const cx = size / 2;
  const cy = size / 2 + 4;
  // Arc spans 225° — from bottom-left (225°) to bottom-right (315°) going clockwise
  const startDeg = 225;
  const sweepDeg = 270;
  const toRad = d => (d * Math.PI) / 180;
  const arcX = (deg) => cx + r * Math.cos(toRad(deg));
  const arcY = (deg) => cy + r * Math.sin(toRad(deg));
  const endDeg = startDeg + sweepDeg * pct;
  const largeArc = sweepDeg * pct > 180 ? 1 : 0;
  const trackEnd = startDeg + sweepDeg;

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ display: 'block' }}>
      {/* Track */}
      <path
        d={`M ${arcX(startDeg)} ${arcY(startDeg)} A ${r} ${r} 0 1 1 ${arcX(trackEnd)} ${arcY(trackEnd)}`}
        fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth={5} strokeLinecap="round"
      />
      {/* Fill */}
      {pct > 0 && (
        <path
          d={`M ${arcX(startDeg)} ${arcY(startDeg)} A ${r} ${r} 0 ${largeArc} 1 ${arcX(endDeg)} ${arcY(endDeg)}`}
          fill="none" stroke={color} strokeWidth={5} strokeLinecap="round"
        />
      )}
      <text x={cx} y={cy + 5} textAnchor="middle" fontSize={13} fill={color} fontWeight={700}
        style={{ fontFamily: 'monospace' }}>
        {Math.round(value)}%
      </text>
    </svg>
  );
}

// ── Rotary knob ───────────────────────────────────────────────────────────────
function Knob({ label, parameter, value, min, max, step = 0.5, unit = '', roomId, dispatch, color = 'var(--green)' }) {
  const dragRef = useRef(null);
  const pct = (value - min) / (max - min);

  // Capture min/max/step/dispatch in a ref so the stable handlers below always
  // see the latest values without being recreated on every prop change.
  const configRef = useRef({ min, max, step, roomId, parameter, dispatch });
  useEffect(() => { configRef.current = { min, max, step, roomId, parameter, dispatch }; });

  const onDown = (e) => {
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    dragRef.current = { startY: clientY, startValue: value, last: value };
    e.preventDefault();

    const { min: mn, max: mx, step: st, roomId: rid, parameter: param, dispatch: disp } = configRef.current;
    const onMove = (ev) => {
      if (!dragRef.current) return;
      const cy = ev.touches ? ev.touches[0].clientY : ev.clientY;
      const dy = dragRef.current.startY - cy;
      const delta = (dy / 180) * (mx - mn);
      const raw = dragRef.current.startValue + delta;
      const snapped = Math.round(raw / st) * st;
      const clamped = parseFloat(Math.max(mn, Math.min(mx, snapped)).toFixed(2));
      if (clamped !== dragRef.current.last) {
        dragRef.current.last = clamped;
        disp({ type: 'SET_RECIPE_TARGET', payload: { roomId: rid, parameter: param, value: clamped } });
      }
    };
    const onUp = () => {
      dragRef.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onUp);
  };

  const SIZE = 68;
  const r = SIZE / 2 - 7;
  const cx = SIZE / 2;
  const cy = SIZE / 2;
  const startDeg = 225;
  const sweepDeg = 270;
  const toRad = d => (d * Math.PI) / 180;
  const px = deg => cx + r * Math.cos(toRad(deg));
  const py = deg => cy + r * Math.sin(toRad(deg));
  const trackEnd = startDeg + sweepDeg;
  const fillDeg = startDeg + sweepDeg * pct;
  const largeArc = sweepDeg * pct > 180 ? 1 : 0;
  const pointerR = r - 5;
  const pointerX = cx + pointerR * Math.cos(toRad(fillDeg));
  const pointerY = cy + pointerR * Math.sin(toRad(fillDeg));

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, userSelect: 'none',
      padding: '8px 8px 6px', borderRadius: 7,
      background: `linear-gradient(160deg, ${color}0f 0%, rgba(10,12,20,0.6) 100%)`,
      border: `1px solid ${color}40`,
      boxShadow: `inset 0 1px 0 rgba(255,255,255,0.04), 0 0 8px ${color}18`,
    }}>
      <span style={{ fontSize: 9, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.6px', textAlign: 'center' }}>
        {label}
      </span>
      <svg
        width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`}
        style={{ cursor: 'ns-resize', display: 'block', touchAction: 'none' }}
        onMouseDown={onDown} onTouchStart={onDown}
      >
        {/* Body */}
        <circle cx={cx} cy={cy} r={r - 1} fill="#1a1f2e" stroke="rgba(255,255,255,0.08)" strokeWidth={1.5} />
        {/* Track */}
        <path
          d={`M ${px(startDeg)} ${py(startDeg)} A ${r} ${r} 0 1 1 ${px(trackEnd)} ${py(trackEnd)}`}
          fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth={4} strokeLinecap="round"
        />
        {/* Fill arc */}
        {pct > 0.005 && (
          <path
            d={`M ${px(startDeg)} ${py(startDeg)} A ${r} ${r} 0 ${largeArc} 1 ${px(fillDeg)} ${py(fillDeg)}`}
            fill="none" stroke={color} strokeWidth={4} strokeLinecap="round"
          />
        )}
        {/* Pointer */}
        <line x1={cx} y1={cy} x2={pointerX} y2={pointerY}
          stroke="white" strokeWidth={2} strokeLinecap="round" opacity={0.85} />
        <circle cx={cx} cy={cy} r={3} fill="rgba(255,255,255,0.6)" />
      </svg>
      <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text)', fontFamily: 'monospace', lineHeight: 1 }}>
        {value}{unit}
      </span>
    </div>
  );
}


const GAUGE_BASE = process.env.PUBLIC_URL + '/assets/equipment/gauges/';

// Rack-mount controller display with LCD number overlay
function ScreenGauge({ src, label, value, unit, devClass, overlayStyle, inlineUnit, valueFontSize }) {
  const lcd = devClass === 'ok' ? '#00e676' : devClass === 'warning' ? '#ffd740' : '#ff5252';
  const unitStyle = {
    fontSize: 'clamp(7px, 1.6vw, 11px)', color: `${lcd}bb`,
    fontFamily: 'monospace', lineHeight: 1.3,
    textShadow: `0 0 6px ${lcd}88`,
  };
  return (
    <div style={{ position: 'relative', lineHeight: 0, borderRadius: 4, overflow: 'hidden', boxShadow: '0 0 6px 1px rgba(0, 230, 118, 0.25), 0 0 1px 1px rgba(0, 230, 118, 0.15)' }}>
      <img src={src} alt={label} style={{ width: '100%', display: 'block' }} />
      <div style={{
        position: 'absolute', display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', pointerEvents: 'none',
        ...overlayStyle,
      }}>
        {inlineUnit ? (
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 2 }}>
            <span style={{
              fontSize: valueFontSize || 'clamp(11px, 2.8vw, 20px)', fontWeight: 900, color: lcd,
              fontFamily: '"Courier New", Courier, monospace',
              textShadow: `0 0 10px ${lcd}cc`,
              lineHeight: 1, letterSpacing: '-0.01em',
            }}>{value}</span>
            <span style={unitStyle}>{unit}</span>
          </div>
        ) : (
          <>
            <div style={{
              fontSize: valueFontSize || 'clamp(11px, 2.8vw, 20px)', fontWeight: 900, color: lcd,
              fontFamily: '"Courier New", Courier, monospace',
              textShadow: `0 0 10px ${lcd}cc`,
              lineHeight: 1, letterSpacing: '-0.01em',
            }}>{value}</div>
            <div style={unitStyle}>{unit}</div>
          </>
        )}
      </div>
    </div>
  );
}


function AutomationPanel({ room, state, dispatch }) {
  const hasA4 = (state.unlockedTech ?? []).includes('A4');
  const automation = room.automation ?? {};

  const hiredManagers = state.workers.filter(w => w.type === 'manager');
  const availableStrains = STRAINS_LIST.filter(s => state.unlockedStrains.includes(s.id));

  const currentManager = state.workers.find(w => w.id === automation.managerId);
  const managerTier = !currentManager ? null
    : currentManager.skill >= 75 ? { label: 'Expert', color: 'var(--green)' }
    : currentManager.skill >= 50 ? { label: 'Competent', color: 'var(--blue)' }
    : currentManager.skill >= 30 ? { label: 'Mediocre', color: 'var(--yellow)' }
    : { label: 'Poor', color: 'var(--red)' };

  if (!hasA4) {
    return (
      <div className="panel" style={{ opacity: 0.6 }}>
        <div className="panel-title">🤖 Automation</div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          Requires A4: AI Environment Optimizer research
        </div>
      </div>
    );
  }

  return (
    <div className="panel">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div className="panel-title" style={{ margin: 0 }}>🤖 Automation</div>
        {automation.enabled && (
          <span style={{ fontSize: 11, color: 'var(--green)', fontWeight: 700 }}>● ACTIVE</span>
        )}
      </div>

      {/* Manager selector */}
      <div style={{ marginBottom: 8 }}>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>Manager</div>
        <select
          style={{ width: '100%', background: 'var(--bg)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: '4px 8px', fontSize: 12 }}
          value={automation.managerId ?? ''}
          onChange={e => dispatch({ type: 'CONFIGURE_AUTOMATION', payload: {
            roomId: room.id,
            managerId: e.target.value || null,
            defaultStrainId: automation.defaultStrainId,
            defaultPlantCount: automation.defaultPlantCount ?? 10,
          }})}
        >
          <option value="">— No manager —</option>
          {hiredManagers.map(m => (
            <option key={m.id} value={m.id}>
              {m.name} (skill {m.skill}) — ${m.currentWage}/wk
            </option>
          ))}
        </select>
        {currentManager && managerTier && (
          <div style={{ fontSize: 11, color: managerTier.color, marginTop: 3 }}>
            {managerTier.label} manager · morale {Math.round(currentManager.morale)}
          </div>
        )}
        {hiredManagers.length === 0 && (
          <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 3 }}>
            No managers hired — go to Workforce to hire one
          </div>
        )}
      </div>

      {/* Strain selector */}
      <div style={{ marginBottom: 8 }}>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>Default Strain</div>
        <select
          style={{ width: '100%', background: 'var(--bg)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: '4px 8px', fontSize: 12 }}
          value={automation.defaultStrainId ?? ''}
          onChange={e => dispatch({ type: 'CONFIGURE_AUTOMATION', payload: {
            roomId: room.id,
            managerId: automation.managerId,
            defaultStrainId: e.target.value || null,
            defaultPlantCount: automation.defaultPlantCount ?? 10,
          }})}
        >
          <option value="">— Select strain —</option>
          {availableStrains.map(s => (
            <option key={s.id} value={s.id}>{s.name}</option>
          ))}
        </select>
        {automation.defaultStrainId && (
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3 }}>
            Seeds: {(state.seedInventory ?? {})[automation.defaultStrainId] ?? 0} available
          </div>
        )}
      </div>

      {/* Plant count */}
      <div style={{ marginBottom: 10 }}>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>Plants per batch</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <button className="btn btn-secondary btn-sm"
            onClick={() => dispatch({ type: 'CONFIGURE_AUTOMATION', payload: { roomId: room.id, managerId: automation.managerId, defaultStrainId: automation.defaultStrainId, defaultPlantCount: Math.max(1, (automation.defaultPlantCount ?? 10) - 5) } })}>−</button>
          <span style={{ fontSize: 13, fontWeight: 700, minWidth: 28, textAlign: 'center' }}>{automation.defaultPlantCount ?? 10}</span>
          <button className="btn btn-secondary btn-sm"
            onClick={() => dispatch({ type: 'CONFIGURE_AUTOMATION', payload: { roomId: room.id, managerId: automation.managerId, defaultStrainId: automation.defaultStrainId, defaultPlantCount: Math.min(room.capacity || 50, (automation.defaultPlantCount ?? 10) + 5) } })}>+</button>
          <span style={{ fontSize: 11, color: 'var(--text-dim)' }}>/ {room.capacity || 50} max</span>
        </div>
      </div>

      {/* Recipe selector */}
      {automation.defaultStrainId && (() => {
        const recipes = getRecipesForStrain(automation.defaultStrainId);
        const tiers = ['basic', 'standard', 'premium'];
        return (
          <div style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 6 }}>Grow Recipe</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {tiers.map(tier => {
                const recipe = recipes.find(r => r.tier === tier);
                if (!recipe) return null;
                const owned = (state.purchasedRecipes ?? []).includes(recipe.id);
                const selected = automation.recipeId === recipe.id;
                const tierColor = tier === 'premium' ? 'var(--green)' : tier === 'standard' ? 'var(--blue)' : 'var(--text-muted)';
                return (
                  <div key={recipe.id} style={{
                    padding: '8px 10px',
                    background: selected ? 'rgba(63,185,80,0.1)' : 'var(--border-light)',
                    border: `1px solid ${selected ? 'var(--green)' : 'var(--border)'}`,
                    borderRadius: 'var(--radius-sm)',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <span style={{ fontSize: 12, fontWeight: 700, color: tierColor, textTransform: 'capitalize' }}>{tier}</span>
                        <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 6 }}>+{recipe.qualityBonus}% quality</span>
                      </div>
                      {owned ? (
                        <button
                          className={`btn btn-sm ${selected ? 'btn-primary' : 'btn-secondary'}`}
                          onClick={() => dispatch({ type: 'CONFIGURE_AUTOMATION', payload: {
                            roomId: room.id,
                            managerId: automation.managerId,
                            defaultStrainId: automation.defaultStrainId,
                            defaultPlantCount: automation.defaultPlantCount,
                            recipeId: selected ? null : recipe.id,
                          }})}
                        >
                          {selected ? '✓ Active' : 'Use'}
                        </button>
                      ) : (
                        <button
                          className="btn btn-primary btn-sm"
                          disabled={state.cash < recipe.price}
                          onClick={() => dispatch({ type: 'BUY_STRAIN_RECIPE', payload: { recipeId: recipe.id } })}
                        >
                          Buy ${recipe.price.toLocaleString()}
                        </button>
                      )}
                    </div>
                    <div style={{ fontSize: 10, color: 'var(--text-dim)', marginTop: 3 }}>{recipe.description}</div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* Status */}
      {automation.enabled ? (
        <div style={{ padding: '8px 10px', background: 'rgba(63,185,80,0.08)', border: '1px solid rgba(63,185,80,0.3)', borderRadius: 'var(--radius-sm)', fontSize: 12 }}>
          ✅ Room is running automatically
        </div>
      ) : (
        <div style={{ padding: '8px 10px', background: 'var(--border-light)', borderRadius: 'var(--radius-sm)', fontSize: 12, color: 'var(--text-muted)' }}>
          {!automation.managerId ? 'Assign a manager to enable automation' : 'Select a strain to enable automation'}
        </div>
      )}
    </div>
  );
}

export function RoomDetailScreen({ state, dispatch }) {
  const [showPlantModal, setShowPlantModal] = useState(false);
  const [showShopModal, setShowShopModal] = useState(false);
  const [shopTab, setShopTab] = useState('equipment'); // 'equipment' | 'recipes'
  const [selectedStrain, setSelectedStrain] = useState('bagseed-og');
  const [plantCount, setPlantCount] = useState(10);
  const [selectedPhenoId, setSelectedPhenoId] = useState(null);
  const [showTransferPicker, setShowTransferPicker] = useState(false);

  // Skip research rooms — they have their own LabDetailScreen
  const selected = state.rooms.find(r => r.id === state.ui.selectedRoomId);
  const room = (selected && selected.type !== 'lab')
    ? selected
    : state.rooms.find(r => r.unlocked && r.type !== 'lab');

  if (!room) {
    return (
      <div className="screen">
        <div className="empty-state"><span className="empty-icon">🌿</span><span>No room selected</span></div>
      </div>
    );
  }

  const isClosed = !!(room.closedUntilDay && state.gameDay <= room.closedUntilDay);

  if (isClosed) {
    const daysLeft = room.closedUntilDay - state.gameDay;
    return (
      <div className="screen">
        {/* Room selector tabs still visible so player can switch rooms */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
          {state.rooms.filter(r => r.unlocked).map(r => (
            <button
              key={r.id}
              className={`btn btn-sm ${r.id === room.id ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => dispatch({ type: 'NAVIGATE', payload: { screen: 'room', roomId: r.id } })}
            >{r.name}</button>
          ))}
        </div>
        <div style={{
          background: 'linear-gradient(135deg, #1a0505 0%, #2a0808 100%)',
          border: '2px solid var(--red)',
          borderRadius: 'var(--radius)',
          padding: '32px 28px',
          maxWidth: 480,
        }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🔒</div>
          <div style={{ fontWeight: 800, fontSize: 18, color: 'var(--red)', marginBottom: 6 }}>
            {room.name} — Closed by Inspection Order
          </div>
          <p style={{ fontSize: 13, color: '#e8a0a0', lineHeight: 1.6, margin: '0 0 16px' }}>
            Health & safety inspectors have ordered this room closed pending review.
            All operations are suspended until the closure period expires.
          </p>
          <div style={{
            background: 'rgba(255,50,50,0.08)',
            border: '1px solid rgba(255,50,50,0.2)',
            borderRadius: 'var(--radius-sm)',
            padding: '10px 14px',
            fontSize: 13,
            color: 'var(--text-muted)',
          }}>
            Reopens <strong style={{ color: 'var(--text)' }}>Day {room.closedUntilDay}</strong>
            {' '}— {daysLeft} day{daysLeft !== 1 ? 's' : ''} remaining
          </div>
        </div>
      </div>
    );
  }

  if (!room.unlocked) {
    return (
      <div className="screen">
        <div className="panel" style={{ maxWidth: 400 }}>
          <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8 }}>🔒 {room.name} — Locked</div>
          <p style={{ color: 'var(--text-muted)', marginBottom: 12 }}>Unlock this room to expand your operation.</p>
          <button
            className="btn btn-primary"
            disabled={state.cash < (room.unlockCost || 5000)}
            onClick={() => dispatch({ type: 'UNLOCK_ROOM', payload: { roomId: room.id } })}
          >
            Unlock for ${(room.unlockCost || 5000).toLocaleString()}
          </button>
        </div>
      </div>
    );
  }

  // ── Department routing ──────────────────────────────────────────────────────
  // Derive department from field or fall back to room type for old saves
  const dept = room.department
    || (room.type === 'curing' || room.type === 'processing' ? 'post-production'
      : room.type === 'lab' ? 'research' : 'production');

  if (dept === 'research') {
    // Should not be reached (room selection filters out lab rooms above)
    return null;
  }

  if (dept === 'post-production') {
    return <PostProductionDetail room={room} state={state} dispatch={dispatch} />;
  }

  const batch = room.batch;
  const env = room.environment;
  const recipe = room.recipe;
  const vpd = calculateVPD(env.temperature, env.humidity);
  const vpdTarget = recipe.vpdTarget || 1.0;
  const vpdDev = Math.abs(vpd - vpdTarget);
  const vpdClass = vpdDev < 0.2 ? 'ok' : vpdDev < 0.5 ? 'warning' : 'danger';

  const availableStrains = STRAINS_LIST.filter(s => state.unlockedStrains.includes(s.id));
  const ambientTint = getAmbientTint(env, batch);

  return (
    <div className="screen">
      {/* Room selector tabs */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
        {state.rooms.filter(r => r.unlocked).map(r => (
          <button
            key={r.id}
            className={`btn ${r.id === room.id ? 'btn-primary' : 'btn-secondary'} btn-sm`}
            onClick={() => dispatch({ type: 'NAVIGATE', payload: { screen: 'room', roomId: r.id } })}
          >
            {r.name}
          </button>
        ))}
      </div>

      <div className="room-detail-grid">
        {/* LEFT col — Environment + Recipe */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, overflowY: 'auto', minHeight: 0 }}>

          {/* Live gauges */}
          <div className="panel" style={{ background: ambientTint ? `linear-gradient(135deg, var(--panel) 60%, ${ambientTint})` : undefined }}>
            <div className="panel-title">Live Environment</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
              {/* Temperature */}
              <ScreenGauge
                src={GAUGE_BASE + 'gauge-temp.png'}
                label="Temperature"
                value={env.temperature.toFixed(1)}
                unit="°C"
                devClass={pctDevClass(env.temperature, recipe.temperature)}
                overlayStyle={{ top: '13%', left: '16%', width: '66%', height: '50%' }}
              />
              {/* Humidity */}
              <ScreenGauge
                src={GAUGE_BASE + 'gauge-humidity.png'}
                label="Humidity"
                value={Math.round(env.humidity)}
                unit="%"
                devClass={pctDevClass(env.humidity, recipe.humidity)}
                overlayStyle={{ top: '24%', left: '8%', width: '58%', height: '36%' }}
                inlineUnit
              />
              {/* VPD */}
              <ScreenGauge
                src={GAUGE_BASE + 'gauge-vpd.png'}
                label="VPD"
                value={vpd.toFixed(2)}
                unit="kPa"
                devClass={vpdClass}
                overlayStyle={{ top: '23%', left: '26%', width: '50%', height: '34%' }}
              />
              {/* CO₂ */}
              <ScreenGauge
                src={GAUGE_BASE + 'gauge-co2.png'}
                label="CO₂"
                value={Math.round(env.co2)}
                unit="ppm"
                devClass={pctDevClass(env.co2, recipe.co2 ?? 800)}
                overlayStyle={{ top: '24%', left: '7%', width: '58%', height: '36%' }}
                inlineUnit
                valueFontSize="clamp(7px, 1.7vw, 13px)"
              />
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 10 }}>
              {/* CSS toggle switch */}
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none' }}>
                <div
                  onClick={() => dispatch({ type: 'TOGGLE_LIGHT', payload: { roomId: room.id } })}
                  style={{
                    position: 'relative', width: 46, height: 26, flexShrink: 0,
                    background: env.lightOn ? (room.type === 'flower' ? '#b347ff' : '#3fb950') : 'rgba(255,255,255,0.12)',
                    borderRadius: 13,
                    transition: 'background 0.25s',
                    boxShadow: env.lightOn ? `0 0 8px ${room.type === 'flower' ? '#b347ff88' : '#3fb95088'}` : 'none',
                  }}
                >
                  <div style={{
                    position: 'absolute', top: 3,
                    left: env.lightOn ? 23 : 3,
                    width: 20, height: 20, borderRadius: '50%',
                    background: 'white',
                    transition: 'left 0.25s',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.4)',
                  }} />
                </div>
                <span style={{ fontSize: 12, color: env.lightOn ? 'var(--text)' : 'var(--text-muted)' }}>
                  {env.lightOn ? '💡 Lights On' : '🌑 Lights Off'}
                </span>
              </label>
              <span style={{ fontSize: 11, color: 'var(--text-dim)' }}>
                {recipe.lightHours}h / {24 - recipe.lightHours}h
              </span>
            </div>
          </div>

          {/* Recipe controls */}
          <div className="panel">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <div className="panel-title" style={{ margin: 0 }}>Recipe Targets</div>
              {batch && (() => {
                const strain = STRAINS_LIST.find(s => s.id === batch.strainId);
                const stage = (batch.stage === 'flowering' || batch.stage === 'harvest') ? 'flower' : 'veg';
                const optima = strain?.[stage];
                if (!optima) return null;
                return (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>
                      {strain.name} ({stage}): {optima.tempOptimal}°C · {optima.humidityOptimal}% · {optima.co2Optimal}ppm
                    </span>
                    <button
                      className="btn btn-secondary btn-sm"
                      style={{ fontSize: 10, whiteSpace: 'nowrap' }}
                      title={`Set recipe to ${strain.name} ${stage} optima`}
                      onClick={() => {
                        dispatch({ type: 'SET_RECIPE_TARGET', payload: { roomId: room.id, parameter: 'temperature', value: optima.tempOptimal } });
                        dispatch({ type: 'SET_RECIPE_TARGET', payload: { roomId: room.id, parameter: 'humidity', value: optima.humidityOptimal } });
                        dispatch({ type: 'SET_RECIPE_TARGET', payload: { roomId: room.id, parameter: 'co2', value: optima.co2Optimal } });
                        dispatch({ type: 'SET_RECIPE_TARGET', payload: { roomId: room.id, parameter: 'lightHours', value: optima.lightOptimal } });
                      }}
                    >
                      🎯 Auto-set
                    </button>
                  </div>
                );
              })()}
            </div>
            {/* Knob grid — drag up to increase, drag down to decrease */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '14px 8px', justifyItems: 'center', padding: '4px 0 8px' }}>
              <Knob label="Temp" parameter="temperature" value={recipe.temperature} min={15} max={35} step={0.5} unit="°C" roomId={room.id} dispatch={dispatch} color="#ff7b54" />
              <Knob label="Humidity" parameter="humidity" value={recipe.humidity} min={30} max={80} step={1} unit="%" roomId={room.id} dispatch={dispatch} color="#58a6ff" />
              {room.equipment.co2sys?.installed
                ? <Knob label="CO₂" parameter="co2" value={recipe.co2 ?? 800} min={400} max={1500} step={50} unit="ppm" roomId={room.id} dispatch={dispatch} color="#3fb950" />
                : <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, opacity: 0.35 }}>
                    <span style={{ fontSize: 9, color: 'var(--text-muted)', textTransform: 'uppercase' }}>CO₂</span>
                    <div style={{ width: 68, height: 68, borderRadius: '50%', background: '#1a1f2e', border: '1px dashed rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <span style={{ fontSize: 18 }}>🔒</span>
                    </div>
                    <span style={{ fontSize: 9, color: 'var(--text-dim)' }}>upgrade</span>
                  </div>
              }
              <Knob label="Light h" parameter="lightHours" value={recipe.lightHours} min={6} max={24} step={1} unit="h" roomId={room.id} dispatch={dispatch} color="#d29922" />
              <Knob label="Nutrient" parameter="nutrientEC" value={recipe.nutrientEC} min={0.5} max={3.0} step={0.1} unit="EC" roomId={room.id} dispatch={dispatch} color="#8b5cf6" />
              <Knob label="pH" parameter="phTarget" value={recipe.phTarget} min={5.5} max={7.0} step={0.1} unit="pH" roomId={room.id} dispatch={dispatch} color="#06b6d4" />
            </div>
          </div>

          {/* Equipment */}
          <div className="panel">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <div className="panel-title" style={{ margin: 0 }}>Equipment Status</div>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.97 }}
                className="btn btn-primary btn-sm"
                onClick={() => setShowShopModal(true)}
              >
                🛒 Shop
              </motion.button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {[
                { key: 'hvac', label: 'HVAC Unit', icon: '⚙️' },
                { key: 'lights', label: 'LED Array', icon: '💡' },
                { key: 'irrigation', label: 'Irrigation', icon: '💧' },
                { key: 'co2sys', label: 'CO₂ System', icon: '🟢', optional: true },
              ].map(({ key, label, icon, optional }) => {
                const equip = room.equipment[key];
                if (!equip && optional) return (
                  <div key={key} className="gauge-card" style={{ opacity: 0.4 }}>
                    <div className="gauge-label">{icon} {label}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Not installed</div>
                  </div>
                );
                if (!equip) return null;
                const health = equip.health;
                const status = health > 70 ? 'good' : health > 40 ? 'degraded' : 'critical';
                return (
                  <div key={key} className="gauge-card" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                    <div className="gauge-label">{icon} {label}</div>
                    <ArcGauge value={health} size={72} />
                    <span style={{ fontSize: 10, color: health > 70 ? 'var(--green)' : health > 40 ? 'var(--yellow)' : 'var(--red)', marginTop: -2 }}>{status}</span>
                    {health < 70 && (
                      <button
                        className="btn btn-warn btn-sm"
                        style={{ marginTop: 6, width: '100%' }}
                        disabled={state.cash < 200}
                        onClick={() => dispatch({ type: 'REPAIR_EQUIPMENT', payload: { roomId: room.id, equipType: key } })}
                      >
                        Repair $200
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* RIGHT col — Batch management */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, overflowY: 'auto', minHeight: 0 }}>

          {batch ? (
            <>
              <div className="batch-panel">
                {/* Plant sprite */}
                {getPlantSprite(batch) && (
                  <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 10 }}>
                    <img
                      src={BASE + getPlantSprite(batch) + CACHE_V}
                      alt={batch.stage}
                      style={{
                        height: 110,
                        width: 'auto',
                        objectFit: 'contain',
                        filter: batch.health < 40
                          ? 'saturate(0.4) sepia(0.4) brightness(0.85)'
                          : batch.thcAccumulation > 70
                          ? 'saturate(1.3) drop-shadow(0 0 6px rgba(160,80,255,0.5))'
                          : 'drop-shadow(0 2px 6px rgba(0,0,0,0.4))',
                        transition: 'filter 0.5s',
                      }}
                    />
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span className={`batch-stage-badge ${batch.stage}`}>{batch.stage}</span>
                  <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Day {Math.floor(batch.dayInStage)}</span>
                </div>

                <div className="stat-row">
                  <span className="lbl">Strain</span>
                  <span className="val" style={{ textTransform: 'capitalize' }}>{batch.strainId.replace(/-/g, ' ')}</span>
                </div>
                <div className="stat-row">
                  <span className="lbl">Plants</span>
                  <span className="val">{batch.plantCount}</span>
                </div>
                <div className="stat-row">
                  <span className="lbl">Total Age</span>
                  <span className="val">{Math.floor(batch.totalAge)} days</span>
                </div>

                <hr className="divider" />

                <ProgressBar value={batch.health} showLabel label="Health" />
                <div style={{ marginTop: 6 }}>
                  <ProgressBar value={100 - batch.stress} showLabel label="Stress resistance" />
                </div>
                <div style={{ marginTop: 6 }}>
                  <ProgressBar value={batch.yieldPotential} showLabel label="Yield Potential" />
                </div>

                {batch.stage === 'flowering' && (
                  <>
                    <div style={{ marginTop: 6 }}>
                      <ProgressBar value={batch.thcAccumulation} showLabel label="THC Accumulation" />
                    </div>
                    <div style={{ marginTop: 6 }}>
                      <ProgressBar value={batch.terpeneProfile} showLabel label="Terpene Profile" />
                    </div>
                  </>
                )}

                <hr className="divider" />

                <div className="stat-row">
                  <span className="lbl">Projected Yield</span>
                  <span className="val text-green fw-700">{batch.projectedGrams}g</span>
                </div>

                {/* Risk indicators */}
                {(batch.moldRisk > 20 || batch.hermaphroditeRisk > 20) && (
                  <div style={{ marginTop: 6, display: 'flex', gap: 6 }}>
                    {batch.moldRisk > 20 && <span className="alert-pill warning">🍄 Mold {Math.round(batch.moldRisk)}%</span>}
                    {batch.hermaphroditeRisk > 20 && <span className="alert-pill high">⚡ Herm {Math.round(batch.hermaphroditeRisk)}%</span>}
                  </div>
                )}
              </div>

              {/* Alerts */}
              {room.alerts?.length > 0 && (
                <div className="panel">
                  <div className="panel-title">Alerts</div>
                  {room.alerts.map((alert, i) => (
                    <div key={i} style={{ marginBottom: 4 }}>
                      <span className={`alert-pill ${alert.severity}`}>{alert.message}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Transfer batch to another room */}
              {batch && !['harvest', 'curing'].includes(batch.stage) && (() => {
                const eligibleRooms = state.rooms.filter(r =>
                  r.unlocked && !r.batch && r.id !== room.id &&
                  !(r.closedUntilDay && state.gameDay <= r.closedUntilDay)
                );
                return (
                  <div style={{ marginBottom: 8 }}>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => setShowTransferPicker(v => !v)}
                    >
                      🌿 Transfer Batch →
                    </button>
                    {showTransferPicker && (
                      <div style={{
                        marginTop: 8, padding: '10px 12px',
                        background: 'var(--border-light)',
                        border: '1px solid var(--border)',
                        borderRadius: 'var(--radius-sm)',
                      }}>
                        <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 8 }}>Choose destination:</div>
                        {eligibleRooms.length === 0 ? (
                          <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>No available rooms. All unlocked rooms have active batches.</div>
                        ) : (
                          eligibleRooms.map(dest => {
                            const isFlowerRoom = dest.recipe?.lightHours <= 13;
                            const willFlower = ['seedling', 'vegetative'].includes(batch.stage) && isFlowerRoom;
                            return (
                              <button
                                key={dest.id}
                                className="btn btn-secondary btn-sm"
                                style={{ display: 'block', width: '100%', marginBottom: 4, textAlign: 'left' }}
                                onClick={() => {
                                  dispatch({ type: 'TRANSFER_BATCH', payload: { fromRoomId: room.id, toRoomId: dest.id } });
                                  setShowTransferPicker(false);
                                }}
                              >
                                {dest.name}
                                <span style={{ color: 'var(--text-dim)', fontSize: 11, marginLeft: 6 }}>
                                  ({dest.type} · {dest.capacity} cap{willFlower ? ' · 🌸 triggers flower' : ''})
                                </span>
                              </button>
                            );
                          })
                        )}
                        <button
                          className="btn btn-secondary btn-sm"
                          style={{ marginTop: 4 }}
                          onClick={() => setShowTransferPicker(false)}
                        >
                          Cancel
                        </button>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Harvest button */}
              {batch.stage === 'harvest' && (
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.97 }}
                  className="btn btn-primary"
                  style={{ width: '100%', padding: '10px' }}
                  onClick={() => dispatch({ type: 'HARVEST_BATCH', payload: { roomId: room.id } })}
                >
                  ✂️ Harvest Batch
                </motion.button>
              )}
            </>
          ) : (
            <div className="batch-panel">
              {room.needsCleaning && (
                <div style={{
                  marginBottom: 12,
                  padding: '8px 12px',
                  background: 'rgba(210, 153, 34, 0.15)',
                  border: '1px solid var(--yellow)',
                  borderRadius: 'var(--radius-sm)',
                  fontSize: 12,
                  color: 'var(--yellow)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}>
                  <span>⚠️</span>
                  <span>Room needs deep cleaning. Assign a worker below and send them to clean before planting for best results.</span>
                </div>
              )}
              <div className="empty-state">
                <span className="empty-icon">🪴</span>
                <span className="empty-title">Room Empty</span>
                <span className="empty-desc">
                  {room.needsCleaning
                    ? 'Clean first, or plant now at a health penalty.'
                    : recipe.lightHours <= 13
                      ? 'Room is set to flower schedule (12h). Assign a worker to set up veg recipe first, or plant directly.'
                      : 'Plant a batch to begin growing.'}
                </span>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.97 }}
                  className="btn btn-primary"
                  style={{ marginTop: 12 }}
                  onClick={() => setShowPlantModal(true)}
                  disabled={Object.values(state.seedInventory).every(v => v === 0)}
                >
                  🌱 Plant Batch
                </motion.button>
              </div>
            </div>
          )}

          {/* Workers assigned — always visible so workers stay in room after harvest */}
          <div className="panel">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <div className="panel-title" style={{ margin: 0 }}>Assigned Workers</div>
              <select
                style={{ background: 'var(--border-light)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', color: 'var(--text)', padding: '3px 6px', fontSize: 11 }}
                value=""
                onChange={e => {
                  if (e.target.value) dispatch({ type: 'ASSIGN_WORKER', payload: { workerId: e.target.value, roomId: room.id } });
                }}
              >
                <option value="">+ Assign worker…</option>
                {state.workers
                  .filter(w => w.assignedRoomId !== room.id)
                  .map(w => (
                    <option key={w.id} value={w.id}>{w.name} ({w.type})</option>
                  ))}
              </select>
            </div>
            {room.assignedWorkers?.length > 0 ? (
              (room.assignedWorkers || []).map(wId => {
                const w = state.workers.find(wr => wr.id === wId);
                if (!w) return null;
                return (
                  <div key={wId} style={{ marginBottom: 8, padding: '8px', background: 'var(--border-light)', borderRadius: 'var(--radius-sm)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                      <span style={{ fontSize: 12, fontWeight: 600 }}>{w.name} <span className={`type-badge ${w.type}`}>{w.type}</span></span>
                      <button className="btn btn-secondary btn-sm" onClick={() => dispatch({ type: 'UNASSIGN_WORKER', payload: { workerId: wId } })}>Remove</button>
                    </div>
                    {/* Manual task buttons — context-sensitive */}
                    {(() => {
                      const isTrimmer = w.type === 'trimmer';
                      const isAustin  = w.id === 'worker-austin';
                      // Available empty rooms for transfer (prefer flower schedule rooms)
                      const transferRooms = state.rooms
                        .filter(r => r.unlocked && !r.batch && r.id !== room.id)
                        .sort((a, _b) => (a.recipe?.lightHours <= 13 ? -1 : 1));
                      const transferTarget = transferRooms[0] || null;
                      return (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {batch && batch.stage === 'flowering' && recipe.lightHours > 13 && (
                        <button
                          className="btn btn-sm"
                          style={{ background: 'var(--red)', color: '#fff', border: 'none' }}
                          onClick={() => dispatch({ type: 'WORKER_TASK', payload: { workerId: wId, roomId: room.id, task: 'flip_lights' } })}
                        >
                          🌙 Flip to 12/12!
                        </button>
                      )}
                      {batch && batch.moldRisk > 10 && (!isTrimmer || isAustin) && (
                        <button
                          className="btn btn-warn btn-sm"
                          title={isTrimmer ? 'Austin will try but trimmers aren\'t great at this — hire a grower' : undefined}
                          onClick={() => dispatch({ type: 'WORKER_TASK', payload: { workerId: wId, roomId: room.id, task: 'clear_mold' } })}
                        >
                          🍄 {isTrimmer ? 'Clear Mold (weak)' : 'Clear Mold'}
                        </button>
                      )}
                      {batch && batch.pestRisk > 10 && (!isTrimmer || isAustin) && (
                        <button
                          className="btn btn-warn btn-sm"
                          title={isTrimmer ? 'Austin will try but trimmers aren\'t great at this — hire a grower' : undefined}
                          onClick={() => dispatch({ type: 'WORKER_TASK', payload: { workerId: wId, roomId: room.id, task: 'treat_pests' } })}
                        >
                          🐛 {isTrimmer ? 'Treat Pests (weak)' : 'Treat Pests'}
                        </button>
                      )}
                      {batch && (batch.stage === 'vegetative' || batch.stage === 'flowering') && (
                        <button className="btn btn-secondary btn-sm" onClick={() => dispatch({ type: 'WORKER_TASK', payload: { workerId: wId, roomId: room.id, task: 'defoliate' } })}>
                          ✂️ Defoliate
                        </button>
                      )}
                      {batch && batch.stage === 'vegetative' && transferTarget && (
                        <button
                          className="btn btn-sm"
                          style={{ background: 'var(--green-dark)', border: '1px solid var(--green)', color: 'var(--green)' }}
                          title={`Transfer to ${transferTarget.name}${transferTarget.recipe?.lightHours <= 13 ? ' (flower room — ideal)' : ''}`}
                          onClick={() => dispatch({ type: 'WORKER_TASK', payload: { workerId: wId, roomId: room.id, task: 'move_plants', toRoomId: transferTarget.id } })}
                        >
                          🌸 Transfer → {transferTarget.name}
                        </button>
                      )}
                      {!batch && (
                        <button
                          className={`btn btn-sm ${room.needsCleaning ? 'btn-warn' : 'btn-secondary'}`}
                          onClick={() => dispatch({ type: 'WORKER_TASK', payload: { workerId: wId, roomId: room.id, task: 'deep_clean' } })}
                        >
                          {room.needsCleaning ? '🧹 Deep Clean Room' : '🌿 Setup Veg Recipe'}
                        </button>
                      )}
                    </div>
                      );
                    })()}
                  </div>
                );
              })
            ) : (
              <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>No workers assigned. Use the dropdown above to assign one.</div>
            )}
          </div>

          {/* Automation panel — only for non-lab rooms */}
          {room.type !== 'lab' && (
            <AutomationPanel room={room} state={state} dispatch={dispatch} />
          )}
        </div>
      </div>

      {/* Equipment Shop Modal */}
      {showShopModal && (
        <Modal
          title="🛒 Equipment Shop"
          onClose={() => setShowShopModal(false)}
          footer={
            <button className="btn btn-secondary" onClick={() => setShowShopModal(false)}>Close</button>
          }
        >
          {/* Tab switcher */}
          <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
            {[['equipment', '⚙️ Equipment'], ['recipes', '🌿 Nutrient Recipes']].map(([key, label]) => (
              <button
                key={key}
                className={`btn btn-sm ${shopTab === key ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setShopTab(key)}
              >
                {label}
              </button>
            ))}
          </div>

          {shopTab === 'equipment' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {EQUIPMENT_CATALOG.map(item => {
                const equip = room.equipment[item.targetEquipment];
                const alreadyInstalled = item.targetEquipment === 'lights'
                  ? equip?.installed && equip?.efficiency >= (item.effect.efficiency || 0)
                  : (item.effect.installed ? equip?.installed : false);
                const isUpgrade = item.targetEquipment === 'lights' && equip?.installed && !alreadyInstalled;
                const canAfford = state.cash >= item.cost;

                return (
                  <div key={item.id} style={{
                    padding: '10px 12px',
                    background: alreadyInstalled ? 'var(--border-light)' : 'var(--panel)',
                    border: `1px solid ${alreadyInstalled ? 'var(--green)' : 'var(--border)'}`,
                    borderRadius: 'var(--radius-sm)',
                    opacity: alreadyInstalled ? 0.65 : 1,
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1, marginRight: 10 }}>
                        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 3 }}>
                          {item.icon} {item.name}
                          {alreadyInstalled && <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--green)' }}>✓ Installed</span>}
                          {isUpgrade && <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--yellow)' }}>↑ Upgrade</span>}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.5 }}>{item.description}</div>
                      </div>
                      <div style={{ textAlign: 'right', flexShrink: 0 }}>
                        <div style={{ fontWeight: 700, color: canAfford ? 'var(--green)' : 'var(--red)', marginBottom: 4 }}>
                          ${item.cost.toLocaleString()}
                        </div>
                        <button
                          className="btn btn-primary btn-sm"
                          disabled={alreadyInstalled || !canAfford}
                          onClick={() => {
                            dispatch({ type: 'BUY_EQUIPMENT', payload: { roomId: room.id, itemId: item.id } });
                            // Don't close — let player see the ✓ Installed confirmation
                          }}
                        >
                          {alreadyInstalled ? 'Installed' : 'Buy'}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {shopTab === 'recipes' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 2 }}>
                Nutrient recipes apply to the current room. Only one package active at a time — new purchase replaces the old one.
              </div>
              {RECIPE_PACKAGES.map(pkg => {
                const recipeFlags = ['bloomBoost', 'stressShield', 'yieldMaximizer'];
                const activeFlag = recipeFlags.find(f => pkg.recipeChanges[f] === true);
                const alreadyActive = activeFlag && room.recipe?.[activeFlag] === true;
                const canAfford = state.cash >= pkg.cost;

                return (
                  <div key={pkg.id} style={{
                    padding: '10px 12px',
                    background: alreadyActive ? 'var(--border-light)' : 'var(--panel)',
                    border: `1px solid ${alreadyActive ? 'var(--green)' : 'var(--border)'}`,
                    borderRadius: 'var(--radius-sm)',
                    opacity: alreadyActive ? 0.65 : 1,
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1, marginRight: 10 }}>
                        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 3 }}>
                          {pkg.icon} {pkg.name}
                          {alreadyActive && <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--green)' }}>✓ Active</span>}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.5 }}>{pkg.description}</div>
                      </div>
                      <div style={{ textAlign: 'right', flexShrink: 0 }}>
                        <div style={{ fontWeight: 700, color: canAfford ? 'var(--green)' : 'var(--red)', marginBottom: 4 }}>
                          ${pkg.cost.toLocaleString()}
                        </div>
                        <button
                          className="btn btn-primary btn-sm"
                          disabled={alreadyActive || !canAfford}
                          onClick={() => {
                            dispatch({ type: 'APPLY_RECIPE', payload: { roomId: room.id, packageId: pkg.id } });
                            // Don't close — let player see the ✓ Active confirmation
                          }}
                        >
                          {alreadyActive ? 'Active' : 'Apply'}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Modal>
      )}

      {/* Plant Batch Modal */}
      {showPlantModal && (
        <Modal
          title="🌱 Plant New Batch"
          onClose={() => { setSelectedPhenoId(null); setShowPlantModal(false); }}
          footer={
            <>
              <button className="btn btn-secondary" onClick={() => { setSelectedPhenoId(null); setShowPlantModal(false); }}>Cancel</button>
              <button
                className="btn btn-primary"
                onClick={() => {
                  dispatch({ type: 'PLANT_BATCH', payload: { roomId: room.id, strainId: selectedStrain, plantCount, phenoId: selectedPhenoId } });
                  setSelectedPhenoId(null);
                  setShowPlantModal(false);
                }}
                disabled={!state.seedInventory[selectedStrain] || state.seedInventory[selectedStrain] < plantCount}
              >
                Plant {plantCount} Seeds
              </button>
            </>
          }
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
              <label style={{ fontSize: 12, color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>Select Strain</label>
              {availableStrains.map(strain => (
                <div
                  key={strain.id}
                  onClick={() => { setSelectedStrain(strain.id); setSelectedPhenoId(null); }}
                  style={{
                    padding: '8px 12px',
                    marginBottom: 4,
                    borderRadius: 'var(--radius-sm)',
                    border: `1px solid ${selectedStrain === strain.id ? 'var(--green)' : 'var(--border)'}`,
                    background: selectedStrain === strain.id ? 'var(--green-dark)' : 'var(--border-light)',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <div>
                    <span style={{ fontWeight: 600, fontSize: 13 }}>{strain.name}</span>
                    <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 8 }}>{strain.description.slice(0, 50)}…</span>
                  </div>
                  <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                    {state.seedInventory[strain.id] || 0} seeds
                  </span>
                </div>
              ))}
            </div>
              {/* Pheno selector — only shown when phenos exist for this strain */}
              {(() => {
                const strainPhenos = (state.phenotypes || []).filter(p => p.strainId === selectedStrain);
                if (strainPhenos.length === 0) return null;
                return (
                  <div style={{ marginBottom: 10 }}>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>Phenotype</div>
                    <select
                      value={selectedPhenoId || ''}
                      onChange={e => setSelectedPhenoId(e.target.value || null)}
                      style={{
                        width: '100%',
                        background: 'var(--border-light)',
                        border: '1px solid var(--border)',
                        color: 'var(--text)',
                        borderRadius: 'var(--radius-sm)',
                        padding: '4px 6px',
                        fontSize: 12,
                      }}
                    >
                      <option value="">Base genetics (no pheno)</option>
                      {strainPhenos.map(p => (
                        <option key={p.id} value={p.id}>
                          {p.name} ({p.rarity})
                        </option>
                      ))}
                    </select>
                  </div>
                );
              })()}
            <div>
              <label style={{ fontSize: 12, color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>
                Plant Count: <strong style={{ color: 'var(--text)' }}>{plantCount}</strong>
              </label>
              <input
                type="range" min={1} max={room.capacity ?? 50} step={1} value={plantCount}
                onChange={e => setPlantCount(parseInt(e.target.value))}
                style={{ width: '100%', accentColor: 'var(--green)' }}
              />
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                Seeds available: {state.seedInventory[selectedStrain] || 0}
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Post-Production Detail (Curing / Processing) ────────────────────────────
function PPSectionHead({ children }) {
  return (
    <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', color: 'var(--text-muted)', textTransform: 'uppercase', marginTop: 18, marginBottom: 8, borderBottom: '1px solid var(--border)', paddingBottom: 4 }}>
      {children}
    </div>
  );
}
function PPStatRow({ label, value, valueColor }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5, fontSize: 13 }}>
      <span style={{ color: 'var(--text-muted)' }}>{label}</span>
      <span style={{ fontWeight: 600, color: valueColor || 'var(--text)' }}>{value}</span>
    </div>
  );
}
function PPMiniBar({ value, color }) {
  const v = Math.max(0, Math.min(100, value || 0));
  const c = color || (v > 60 ? 'var(--green)' : v > 30 ? 'var(--yellow)' : 'var(--red)');
  return (
    <div style={{ height: 6, background: 'var(--border)', borderRadius: 3, overflow: 'hidden', marginTop: 3 }}>
      <div style={{ height: '100%', width: `${v}%`, background: c, borderRadius: 3, transition: 'width 0.3s' }} />
    </div>
  );
}

function PostProductionDetail({ room, state, dispatch }) {
  const roomTabs = (
    <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
      {state.rooms.filter(r => r.unlocked).map(r => (
        <button
          key={r.id}
          className={`btn btn-sm ${r.id === room.id ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => dispatch({ type: 'NAVIGATE', payload: { screen: 'room', roomId: r.id } })}
        >{r.name}</button>
      ))}
    </div>
  );

  if (room.type === 'curing') return <CuringDetail room={room} state={state} dispatch={dispatch} tabs={roomTabs} />;
  if (room.type === 'processing') return <ProcessingDetail room={room} state={state} dispatch={dispatch} tabs={roomTabs} />;

  return (
    <div className="screen">
      {roomTabs}
      <div className="panel"><div style={{ fontWeight: 700 }}>{room.name}</div></div>
    </div>
  );
}

function CuringDetail({ room, state, dispatch, tabs }) {
  const env    = room.environment ?? {};
  const recipe = room.recipe ?? { temperature: 18, humidity: 62 };
  const eq     = room.equipment ?? {};
  const curingSlots = room.curingSlots ?? [];

  const tempDev = recipe.temperature ? Math.abs((env.temperature ?? recipe.temperature) - recipe.temperature) / recipe.temperature * 100 : 0;
  const humDev  = recipe.humidity    ? Math.abs((env.humidity ?? recipe.humidity) - recipe.humidity) / recipe.humidity * 100 : 0;
  const condBadge = tempDev < 5 && humDev < 5 ? '🟢 Optimal'
    : tempDev < 15 && humDev < 15 ? '🟡 Acceptable' : '🔴 Poor';

  const curable = state.inventory.filter(i =>
    i.status !== 'sold' && i.status !== 'in_curing' && i.status !== 'in_processing'
    && (i.type ?? 'flower') === 'flower' && i.grams > 0
  );

  return (
    <div className="screen">
      {tabs}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, maxWidth: 800 }}>
        {/* Left column: environment + equipment */}
        <div className="panel">
          <PPSectionHead>Curing Environment</PPSectionHead>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
            <div style={{ background: 'var(--border-light)', borderRadius: 6, padding: '10px 12px', textAlign: 'center' }}>
              <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 3 }}>Temperature</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: tempDev < 5 ? 'var(--green)' : tempDev < 15 ? 'var(--yellow)' : 'var(--red)' }}>
                {(env.temperature ?? recipe.temperature).toFixed(1)}°C
              </div>
              <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>target {recipe.temperature}°C</div>
            </div>
            <div style={{ background: 'var(--border-light)', borderRadius: 6, padding: '10px 12px', textAlign: 'center' }}>
              <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 3 }}>Humidity</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: humDev < 5 ? 'var(--green)' : humDev < 15 ? 'var(--yellow)' : 'var(--red)' }}>
                {Math.round(env.humidity ?? recipe.humidity)}%
              </div>
              <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>target {recipe.humidity}%</div>
            </div>
          </div>
          <PPStatRow label="Condition" value={condBadge} />

          <PPSectionHead>Equipment</PPSectionHead>
          {!eq.humidifier?.installed ? (
            <div style={{ marginBottom: 8 }}>
              <PPStatRow label="Humidifier" value={`$${(eq.humidifier?.cost ?? 4000).toLocaleString()}`} valueColor="var(--text-muted)" />
              <button
                className="btn btn-secondary btn-sm"
                style={{ width: '100%', marginTop: 4 }}
                disabled={state.cash < (eq.humidifier?.cost ?? 4000)}
                onClick={() => dispatch({ type: 'BUY_CURING_EQUIPMENT', payload: { equipId: 'humidifier' } })}
              >
                Buy Humidifier — ${(eq.humidifier?.cost ?? 4000).toLocaleString()}
              </button>
            </div>
          ) : (
            <PPStatRow label="Humidifier" value="✓ Installed" valueColor="var(--green)" />
          )}
        </div>

        {/* Right column: cure slots */}
        <div className="panel">
          <PPSectionHead>Cure Slots ({curingSlots.length}/4)</PPSectionHead>
          {curingSlots.length === 0 && (
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12, lineHeight: 1.5 }}>
              No items curing. Add flower from your inventory below.
            </div>
          )}
          {curingSlots.map(slot => (
            <div key={slot.inventoryId} style={{ background: 'var(--border-light)', borderRadius: 8, padding: '10px 12px', marginBottom: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <span style={{ fontWeight: 600, fontSize: 13 }}>{slot.strainId.replace(/-/g, ' ')}</span>
                <span style={{ fontSize: 12, color: slot.status === 'degrading' ? 'var(--red)' : 'var(--text-muted)' }}>
                  Day {slot.curingDay}/14
                </span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 6, fontSize: 12 }}>
                <span style={{ color: 'var(--text-muted)' }}>Terpenes: <b>{slot.terpeneProfile?.toFixed(1)}</b></span>
                <span style={{ color: 'var(--text-muted)' }}>Visual: <b>{slot.visualAppeal?.toFixed(1)}</b></span>
              </div>
              <PPMiniBar value={(slot.curingDay / 14) * 100} color={slot.status === 'degrading' ? 'var(--red)' : 'var(--green)'} />
              {slot.status === 'degrading' && (
                <div style={{ fontSize: 11, color: 'var(--red)', marginTop: 4 }}>⚠ Mold risk: {slot.moldRisk.toFixed(0)}%</div>
              )}
              <button
                className="btn btn-secondary btn-sm"
                style={{ width: '100%', marginTop: 8 }}
                onClick={() => dispatch({ type: 'PULL_FROM_CURE', payload: { inventoryId: slot.inventoryId } })}
              >
                Pull from Cure
              </button>
            </div>
          ))}

          {curingSlots.length < 4 && curable.length > 0 && (
            <>
              <PPSectionHead>Add to Cure</PPSectionHead>
              {curable.map(item => (
                <button
                  key={item.id}
                  className="btn btn-secondary btn-sm"
                  style={{ width: '100%', marginBottom: 6, display: 'flex', justifyContent: 'space-between' }}
                  onClick={() => dispatch({ type: 'ADD_TO_CURE', payload: { inventoryId: item.id } })}
                >
                  <span>{item.strainId.replace(/-/g, ' ')}</span>
                  <span style={{ color: 'var(--text-muted)' }}>{item.grams}g · Q{item.qualityScore}</span>
                </button>
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ProcessingDetail({ room, state, dispatch, tabs }) {
  const eq    = room.equipment ?? {};
  const queue = room.processingQueue ?? [];

  const hasRosin = eq.rosinPress?.installed;
  const hasBHO   = eq.bhoExtractor?.installed;
  const hasE1    = state.unlockedTech?.includes('E1');
  const hasE2    = state.unlockedTech?.includes('E2');

  const available = state.inventory.filter(i =>
    i.status !== 'sold' && i.status !== 'in_processing' && i.status !== 'in_curing'
    && i.grams > 0
  );

  // Map active processing job to LabViewport format
  const activeJob = queue.find(j => j.status === 'active');
  const viewportItem = activeJob && activeJob.mode === 'extract' && activeJob.extractType ? {
    type: activeJob.extractType,
    strainName: (state.inventory.find(i => i.id === activeJob.inventoryId)?.strainId || 'unknown').replace(/-/g, ' '),
    gramsInput: activeJob.inputGrams,
    gramsOutput: Math.round(activeJob.inputGrams * (activeJob.yieldRate || 0.18)),
    status: 'processing',
    queuePhase: 'active',
  } : null;
  const viewportProgress = activeJob ? activeJob.daysAccumulated / activeJob.daysRequired : null;

  return (
    <div className="screen">
      {tabs}
      {/* Processing Bay viewport */}
      <div style={{ maxWidth: 800, marginBottom: 14 }}>
        <LabViewport
          activeItem={viewportItem}
          tick={state.tick ?? 0}
          progressOverride={viewportProgress}
          idleSubtext={queue.length === 0
            ? 'Add inventory to the queue below to begin processing.'
            : activeJob?.mode === 'packaging' ? `📦 Packaging in progress — Day ${activeJob.daysAccumulated}/${activeJob.daysRequired}` : undefined
          }
        />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, maxWidth: 800 }}>
        {/* Left column: equipment + add to queue */}
        <div className="panel">
          <PPSectionHead>Equipment</PPSectionHead>
          <PPStatRow label="Basic Extractor" value="✓ Installed" valueColor="var(--green)" />
          {[
            { id: 'rosinPress', label: 'Rosin Press', requires: 'E1 research', techCheck: hasE1 },
            { id: 'bhoExtractor', label: 'BHO Extractor', requires: 'E2 research', techCheck: hasE2 },
          ].map(({ id, label, requires, techCheck }) => {
            const e = eq[id];
            return (
              <div key={id} style={{ marginBottom: 8 }}>
                <PPStatRow
                  label={label}
                  value={e?.installed ? '✓ Installed' : `$${(e?.cost ?? 0).toLocaleString()}`}
                  valueColor={e?.installed ? 'var(--green)' : 'var(--text-muted)'}
                />
                {!e?.installed && (
                  <button
                    className="btn btn-secondary btn-sm"
                    style={{ width: '100%', marginTop: 4 }}
                    disabled={!techCheck || state.cash < (e?.cost ?? 0)}
                    onClick={() => dispatch({ type: 'BUY_PROCESSING_EQUIPMENT', payload: { equipId: id } })}
                  >
                    {techCheck ? `Purchase — $${(e?.cost ?? 0).toLocaleString()}` : `Requires ${requires}`}
                  </button>
                )}
              </div>
            );
          })}

          {queue.length < 5 && available.length > 0 && (
            <>
              <PPSectionHead>Add to Queue</PPSectionHead>
              {available.map(item => (
                <div key={item.id} style={{ marginBottom: 8, background: 'var(--border-light)', borderRadius: 8, padding: '8px 10px' }}>
                  <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
                    {item.strainId.replace(/-/g, ' ')} · {item.grams}g
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <button
                      className="btn btn-secondary btn-sm"
                      style={{ flex: 1 }}
                      onClick={() => dispatch({ type: 'ADD_TO_PROCESSING', payload: { inventoryId: item.id, mode: 'packaging', extractType: null } })}
                    >
                      📦 Package
                    </button>
                    {hasE1 && hasRosin && (
                      <button className="btn btn-secondary btn-sm" style={{ flex: 1 }}
                        onClick={() => dispatch({ type: 'ADD_TO_PROCESSING', payload: { inventoryId: item.id, mode: 'extract', extractType: 'rosin' } })}
                      >💎 Rosin</button>
                    )}
                    {hasE2 && hasBHO && (
                      <>
                        <button className="btn btn-secondary btn-sm" style={{ flex: 1 }}
                          onClick={() => dispatch({ type: 'ADD_TO_PROCESSING', payload: { inventoryId: item.id, mode: 'extract', extractType: 'wax' } })}
                        >Wax</button>
                        <button className="btn btn-secondary btn-sm" style={{ flex: 1 }}
                          onClick={() => dispatch({ type: 'ADD_TO_PROCESSING', payload: { inventoryId: item.id, mode: 'extract', extractType: 'shatter' } })}
                        >Shatter</button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </>
          )}
        </div>

        {/* Right column: processing queue */}
        <div className="panel">
          <PPSectionHead>Processing Queue ({queue.length}/5)</PPSectionHead>
          {queue.length === 0 && (
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12, lineHeight: 1.5 }}>
              Queue empty. Add inventory items from the left panel to begin processing.
            </div>
          )}
          {queue.map(job => (
            <div key={job.inventoryId} style={{ background: 'var(--border-light)', borderRadius: 8, padding: '10px 12px', marginBottom: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <span style={{ fontWeight: 600, fontSize: 13 }}>
                  {job.mode === 'packaging' ? '📦 Packaging' : `💎 ${job.extractType ?? 'Extract'}`}
                </span>
                <span style={{ fontSize: 12, color: job.status === 'active' ? 'var(--green)' : 'var(--text-muted)' }}>
                  {job.status === 'active' ? `Day ${job.daysAccumulated}/${job.daysRequired}` : 'Pending'}
                </span>
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>{job.inputGrams}g input</div>
              {job.status === 'active' && (
                <PPMiniBar value={(job.daysAccumulated / job.daysRequired) * 100} color="var(--blue)" />
              )}
              <button
                className="btn btn-secondary btn-sm"
                style={{ width: '100%', marginTop: 8 }}
                onClick={() => dispatch({ type: 'CANCEL_PROCESSING', payload: { inventoryId: job.inventoryId } })}
              >
                Cancel
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
